<template>
    <div
        v-if="!state.giftredeemComplete"
        class="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-16"
    >
        <div class="col order-1 lg:col-span-2">
            <h1 class="title">Redeem</h1>
        </div>
        <div class="col order-2 lg:order-3 relative flex flex-col gap-6">
            <div
                v-if="cart.selectedPlan"
                class="bg-white rounded-md p-4 text-sm"
            >
                <h3 class="font-bold text-xl capitalize pt-2 pb-4 text-green-700 text-center">
                    Your Gift Code is valid!
                </h3>
                <div class="px-4 border-t border-gray-300 py-2 flex items-center justify-between">
                    <div class="">Plan</div>
                    <div class="font-bold">
                        {{ cart.selectedPlan.label }} Plan - <span class="capitalize">{{ cart.selectedPlan.billing_interval }}</span>
                    </div>
                </div>
                <div class="px-4 border-t border-gray-300 py-2 flex items-center justify-between">
                    <div class="">Code</div>
                    <div class="font-bold">{{ state.giftData?.code }}</div>
                </div>
                <div class="px-4 border-t border-gray-300 py-2 flex items-center justify-between">
                    <div class="">From</div>
                    <div class="font-bold">{{ state.giftData?.email }}</div>
                </div>
                <div class="px-4 border-t border-gray-300 py-2 flex items-center justify-between">
                    <div class="font-bold">Total Due</div>
                    <div class="font-bold">Free</div>
                </div>
            </div>

            <template v-if="!state.giftData">
                <h3 class="font-bold text-2xl">Enter your gift code to begin</h3>
                <form
                    class=""
                    @submit.prevent="handleCheckCode"
                >
                    <label for="giftCode">Gift Code</label>
                    <div class="mt-2 w-full flex items-center gap-4">
                        <input
                            id="giftCode"
                            type="text"
                            v-model="store.$state.giftCode"
                            class="form-input"
                            placeholder="Gift Code"
                            required
                        />
                        <button
                            type="submit"
                            class="button button--primary w-32"
                            :disabled="!store.$state.giftCode || state.isLoading"
                        >
                            Apply
                        </button>
                    </div>
                </form>
            </template>
            <div
                v-else-if="state.showCustomerSupport"
                class="p-6  bg-white m-1 rounded-md"
            >
                <h3 class="font-bold text-lg text-red-700">We're sorry!</h3>
                <p class="my-3 text-lg">
                    We are aware of this issue involving inactive accounts and working on a fix.
                </p>
                <p class=" bg-green-50 p-3 rounded-md text-sm">
                    Don't worry, <strong class="text-green-700">your gift code is valid</strong>
                </p>
                <p class="my-3">
                    Please contact
                    <a
                        :href="`mailto:support@winreality.com?subject=Gift%20Code%20Redemption%20Issue:%20${state.giftData?.code}`"
                        class="text-primary-500 font-bold hover:underline"
                    >support@winreality.com</a>
                    to redeem your gift code immediately or try back later.
                </p>

                <a
                    class="button button--primary mt-6"
                    :href="`mailto:support@winreality.com?subject=Gift%20Code%20Redemption%20Issue:%20${state.giftData?.code}`"
                >
                    Contact Support
                </a>
            </div>
            <div
                v-else-if="state.giftData && !state.giftredeemComplete"
                class=""
            >
                <h3 class="font-bold text-2xl">{{ state.redeemSteps[1].title }}</h3>
                <p class="my-3">
                    {{ state.redeemSteps[1].description }}
                </p>
                <LoginStep
                    v-show="state.onStep === 2 && !state.showCustomerSupport"
                    :mode="state.accountMode"
                    @complete="(e) => handleLoginStepComplete(e)"
                    @error="handleLoginStepError"
                />
                <div v-if="state.onStep === 3">
                    <p class="my-3">
                        Processing your gift code...
                    </p>
                </div>
                <p v-if="state.accountMode === 'new'" class="font-bold text-sm">
                    Already have an account?
                    <button
                        class="text-blue-500 hover:underline ml-2" @click="handleAccountMode('existing')">
                        Login
                    </button>
                </p>
                <p v-else class="font-bold text-sm">
                    Don't have an account?
                    <button
                        class="text-blue-500 hover:underline ml-2" @click="handleAccountMode('new')">
                        Create Account
                    </button>
                </p>
            </div>

            <!-- error -->
            <div
                v-if="state.error"
                class="bg-red-100 -mt-1 border-red-800 text-red-800 border my-6 p-4 rounded-md"
            >
                {{ state.error }}
            </div>

            <!-- loading -->
            <AppLoading v-if="state.isLoading" />
        </div>
        <div class="order-3 lg:row-span-2">
            <div
                v-for="step in state.redeemSteps"
                :key="step.number"
                class="flex flex-col mt-5"
            >
                <div class="flex items-center">
                    <div
                        class="h-[40px] w-[40px] rounded-full flex items-center justify-center text-white text-xl font-bold mr-4"
                        :class="{
                            'bg-black': !step.complete || !step.active,
                            'bg-primary-500': step.active || step.complete,
                        }"
                    >
                        <CheckCircleIcon v-if="step.complete" class="h-[40px] w-[40px]" />
                        <template v-else>
                            {{ step.number }}
                        </template>
                    </div>
                    <div class="flex-1">
                        <h2 class="font-bold checkout-step-title">{{ step.title }}</h2>
                    </div>
                </div>
                <p class="ml-14">{{ step.description }}</p>
            </div>
        </div>
    </div>
    <GiftRedeemComplete
        v-else-if="state.giftredeemComplete"
        :redirectUrl="state.giftredeemComplete.redirect_url"
    />
</template>

<script setup lang="ts">
import { useCheckoutStore } from '@/store/checkout'
import { useUserStore } from '@/store/user'
import { useCartStore } from '@/store/cart'
import { reactive } from 'vue'
import { checkGiftCode, redeemGiftCode } from '@/api/gift'
import { getPlanByStripePriceId } from '@/utils/plans'
import { CheckCircleIcon } from '@heroicons/vue/24/solid'
import LoginStep from '@/components/Shared/LoginStep.vue'
import { splitName } from '@/utils/strings'
import { v4 as uuid } from 'uuid'
import AppLoading from '@/components/App/AppLoading.vue'
import GiftRedeemComplete from './GiftRedeemComplete.vue'

const params = new URLSearchParams(window.location.search)

const giftCode: string = params.get('code') || ''
const WIN_DASHBOARD_URL = import.meta.env.VITE_WIN_DASHBOARD_URL || 'https://dashboard.winreality.com'

const store = useCheckoutStore()
const userStore = useUserStore()
const cart = useCartStore()
const state = reactive({
    isLoading: false,
    giftData: null as null | GiftCheckResponse,
    error: null as null | string,
    accountMode: 'new' as 'new' | 'existing',
    redeemSteps: [
        {
            number: '1',
            title: 'Enter gift code',
            description: 'Enter your gift code.',
            active: true,
            complete: false,
        },
        {
            number: '2',
            title: 'Create account',
            description: 'Create your WIN account.',
            active: false,
            complete: false,
        },
        {
            number: '3',
            title: 'Set up player profile',
            description: 'Use the link to the Dashboard on the confirmation page to set up your WIN advanced profile for the best hitting experience!',
            active: false,
            complete: false,
        },
        {
            number: '4',
            title: 'Download VR App',
            description: 'Download the WIN Reality app on your Quest device and log in using your account information.',
            active: false,
            complete: false,
        },
    ],
    giftredeemComplete: null as null | GiftRedeemResponse,
    onStep: 1,
    showCustomerSupport: false,
})

store.$state.giftCode = giftCode
store.$state.mode = 'redeem'

const handleCheckCode = async () => {
    state.isLoading = true
    state.error = null
    try {
        const result = await checkGiftCode(store.$state.giftCode)
        const { valid, price_id } = result
        if (!valid) {
            state.error = 'Invalid gift code'
            return
        }
        const plan = getPlanByStripePriceId(price_id)
        cart.$patch({
            plan: price_id,
            planOptions: [plan],
        })
        state.giftData = result
        state.redeemSteps[0].complete = true
        state.redeemSteps[1].active = true
        state.onStep = 2
    } catch (e: string | any) {
        state.error = e?.message || e || 'Invalid gift code'
    } finally {
        state.isLoading = false
    }
}

const handleAccountMode = (value: 'new'|'existing') => {
    state.accountMode = value
    if (value === 'new') {
        state.redeemSteps[1].title = 'Create account'
        state.redeemSteps[1].description = 'Create your WIN account.'
        return
    }
    if (value === 'existing') {
        state.redeemSteps[1].title = 'Login to your account'
        state.redeemSteps[1].description = 'Login to your WIN account to apply your gift.'
    }
}

const handleLoginStepError = async () => {
    state.redeemSteps[1].complete = true
    state.showCustomerSupport = true
}

const handleLoginStepComplete = async (loginComplete: any) => {
    if (!state.giftData?.code) {
        state.error = 'Missing gift code'
        return
    }

    state.onStep = 3
    state.error = null
    state.isLoading = true

    try {
        let first_name = undefined
        let last_name = undefined
        let username = undefined

        if (state.accountMode === 'new') {
            const res = splitName(loginComplete.name)
            first_name = res.first_name
            last_name = res.last_name
            username = uuid()
        }

        const redeemParams: GiftRedeemParams = {
            promo_code: state.giftData?.code,
            email: loginComplete?.email || store.$state.email,
            password: loginComplete?.password || store.$state.password,
            first_name,
            last_name,
            username,
        }

        const result: GiftRedeemResponse = await redeemGiftCode(redeemParams, loginComplete?.access_token)
        if (!result?.redirect_url) {
            result.redirect_url = WIN_DASHBOARD_URL
        }
        state.redeemSteps[2].complete = true
        state.redeemSteps[3].active = true
        state.giftredeemComplete = result
        state.onStep = 4
        try {
            const loginResponse = await userStore.login({
                username: redeemParams.email,
                password: redeemParams.password,
            })
            if (loginResponse?.access_token) {
                localStorage.setItem('access_token', loginResponse.access_token)
            }
            await userStore.userInfo()
        } catch (e: string | any) {
            console.error(e)
        }
        window.scrollTo(0, 0)
    } catch (error: string | any) {
        state.error = error?.message || error || 'Invalid gift code'
        state.onStep = 2
        if (state.accountMode !== 'new' && loginComplete?.access_token) {
            handleLoginStepError()
        }
    } finally {
        state.isLoading = false
    }
}
</script>

<style>
h2.checkout-step-title {
    font-size: 18px !important;
    font-weight: 700 !important;
    letter-spacing: normal !important;
}
</style>